import {
  apiService,
  createUserAgentString,
  deriveToken,
  Token,
} from "@blacknut/javascript-sdk/dist";
import { State } from "core/redux";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import getEnv from "./getEnv";
import { logD, logE } from "@blacknut/logging/dist";
import { LOGGING_TAG } from "./Utils";
import { useCallback } from "react";
import { firstValueFrom } from "rxjs";

const generateSignupURL = async (
  uri: URL,
  audid: string | undefined,
  platform: string | undefined,
  sessionId: string,
  userToken: Token | undefined,
  familyToken: Token | undefined,
  returnUrl?: URL, // URL which the user is redirected to after clicking on the CTA of the /offers or /pass pages
  sParams?: string,
) => {
  const params: Record<string, string> | undefined = sParams
    ? JSON.parse(sParams)
    : undefined;
  returnUrl && uri.searchParams.append("returnUrl", returnUrl.toString());
  uri.searchParams.append("sid", sessionId);
  uri.searchParams.append("env", getEnv(platform));
  uri.searchParams.append("device", apiService.userAgent.deviceType.toLowerCase());
  !params?.utm_medium &&
    uri.searchParams.append("utm_medium", apiService.userAgent.deviceType.toLowerCase());
  apiService.userAgent.brand &&
    uri.searchParams.append("brand", apiService.userAgent.brand.toLowerCase());
  apiService.userAgent.model &&
    uri.searchParams.append("model", apiService.userAgent.model.toLowerCase());
  audid && uri.searchParams.append("audid", audid);
  userToken?.accessToken && uri.searchParams.append("token", userToken?.accessToken); // FIXME: Might be useless and replaceable by "uat" query param, to be checked with TH

  if (!returnUrl) {
    if (userToken && familyToken) {
      let _userToken = userToken;
      let _familyToken = familyToken;

      try {
        const derived = await firstValueFrom(deriveToken("profile"));
        if (derived.userToken && derived.familyToken) {
          _userToken = derived.userToken;
          _familyToken = derived.familyToken;
        }
      } catch (e) {
        logE(LOGGING_TAG, "Caught error on token derivation", e);
      }
      if (_familyToken) {
        uri.searchParams.append("fat", _familyToken.accessToken);
        uri.searchParams.append("frt", _familyToken.refreshToken);
      }
      if (_userToken) {
        uri.searchParams.append("uat", _userToken.accessToken);
        uri.searchParams.append("urt", _userToken.refreshToken);
      }
    }
  }

  uri.searchParams.append("ua", createUserAgentString(apiService.userAgent));
  const brand = apiService.userAgent.brand;
  if (!params?.source) {
    if (brand === "Samsung" && apiService.userAgent.deviceType === "TV") {
      // Do't ask me why
      uri.searchParams.append("source", "samsungTV");
      !params?.utm_source && uri.searchParams.append("utm_source", "samsungTV");
    } else if (brand) {
      uri.searchParams.append("source", brand);
      !params?.utm_source && uri.searchParams.append("utm_source", brand);
    }
  }
  if (params?.uuid && !params.bundle) {
    uri.searchParams.append("bundle", "false");
  }
  if (params) {
    for (const key of Object.keys(params)) {
      const val = params[key];
      if (val) {
        uri.searchParams.append(key, val);
      }
    }
  }
  logD(LOGGING_TAG, "Using signup url: %o", uri.toString());
  return uri;
};
const useSignupURL = (params?: Record<string, string | undefined>) => {
  const { i18n } = useTranslation();
  const { audid, platform, sessionId, userToken, familyToken } = useSelector(
    (state: State) => state.globalState,
  );

  const sParams = JSON.stringify(params);
  const createSubURL = useCallback(
    (returnUrl?: URL) =>
      generateSignupURL(
        new URL(
          `https://www.blacknut.com/${i18n.language.split("-")[0]}${
            params?.uuid ? "/pass" : "/offers"
          }`,
        ),
        audid,
        platform,
        sessionId,
        userToken,
        familyToken,
        returnUrl,
        sParams,
      ),
    [
      i18n.language,
      params?.uuid,
      audid,
      platform,
      sessionId,
      userToken,
      familyToken,
      sParams,
    ],
  );
  const enrichSubURL = useCallback(
    async (url: URL) =>
      generateSignupURL(
        url,
        audid,
        platform,
        sessionId,
        userToken,
        familyToken,
        undefined,
        sParams,
      ),
    [audid, familyToken, platform, sParams, sessionId, userToken],
  );
  return {
    createSubURL,
    enrichSubURL,
  };
};

export { useSignupURL };
